import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import anime from 'animejs/lib/anime.es.js';
import { ChevronLeft, ChevronRight, ExternalLink, Instagram } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLanguage } from '../contexts/LanguageContext';
import SponsorReel from '../components/SponsorReel';

// Wave painting background for WITHIN
const WAVE_BG = 'https://customer-assets.emergentagent.com/job_7ae2fa0d-fc89-4b84-8ee2-77e1b09b9f84/artifacts/97l9ougt_BG_LEZIN_WAVE_03_C.png';
// WITHIN event poster
const WITHIN_POSTER = 'https://customer-assets.emergentagent.com/job_7ae2fa0d-fc89-4b84-8ee2-77e1b09b9f84/artifacts/0m94fbr8_WITHIN%20-%20Date%20and%20location%20first%20but%20sponsors%20at%20the%20end.png';

// All WITHIN artists organized by role
const MUSIC_ARTISTS = [
  { name: 'Dimitri Andreas', instagram: 'dimitriandreasmusic' },
  { name: 'Alexander Stordiau', instagram: 'alexanderstordiau' },
  { name: 'fromnow', instagram: 'fromnowmusic' },
  { name: 'Léo Patt', instagram: '___leo.patt___' },
];

const VISUALS_ARTISTS = [
  { name: 'Siebe Goeteyn', instagram: 'siebegoeteyn' },
  { name: 'Ziebe Van Mulders', instagram: '_ziebe_' },
];

const LIGHTS_ARTISTS = [
  { name: 'Tom Lagast', instagram: 'kunst_licht___' },
];

const SPONSORS = [
  { name: 'Metro Club', instagram: 'metroclub.ath' },
  { name: 'Leddis', instagram: 'leddis.officiel' },
];

const POWERED_BY = [
  { name: 'Momento', instagram: 'for.momento' },
  { name: 'Nina', instagram: 'bynina.vdr' },
  { name: 'Urban Echoes', instagram: 'urban.echoes__' },
  { name: 'Fairflow', instagram: 'fairflowapp' },
];

// Artist Box Component - simpler style like Metro Club
const ArtistBox = ({ artist }) => (
  <a 
    href={`https://instagram.com/${artist.instagram}`}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`artist-box-${artist.instagram}`}
    style={{
      padding: '20px 18px',
      borderBottom: '1px solid rgba(240,244,248,0.05)',
      borderRight: '1px solid rgba(240,244,248,0.05)',
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      textDecoration: 'none',
      background: 'transparent',
      transition: 'background 0.3s',
    }}
    onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,240,255,0.03)'}
    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
  >
    <span style={{
      fontFamily: 'Syne, sans-serif', 
      fontWeight: 700,
      fontSize: 'clamp(13px, 1.6vw, 16px)',
      letterSpacing: '0.04em', 
      textTransform: 'uppercase',
      color: artist.name === 'fromnow' ? 'var(--vvs-cyan)' : '#F0F4F8',
    }}>
      {artist.name}
    </span>
    <Instagram size={14} style={{ color: 'var(--vvs-cyan)', opacity: 0.7 }} />
  </a>
);

// Sponsor/Partner link component
const PartnerLink = ({ name, handle }) => (
  <a 
    href={`https://instagram.com/${handle}`} 
    target="_blank" 
    rel="noopener noreferrer"
    data-testid={`partner-${handle}`}
    style={{
      color: 'var(--vvs-muted)',
      textDecoration: 'none',
      transition: 'color 0.3s',
      fontFamily: 'Space Grotesk',
      fontSize: '14px',
    }}
    onMouseEnter={e => e.currentTarget.style.color = 'var(--vvs-cyan)'}
    onMouseLeave={e => e.currentTarget.style.color = 'var(--vvs-muted)'}
  >
    {name}
  </a>
);

export default function EventWithin() {
  const { t } = useLanguage();
  const heroRef = useRef(null);
  const storyRef = useScrollReveal({ delay: 0 });
  const lineupRef = useScrollReveal({ delay: 0 });
  const creditsRef = useScrollReveal({ delay: 0 });

  useEffect(() => {
    document.title = 'WITHIN // 360° Immersive Experience // VVS';
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || !heroRef.current) return;

    anime.timeline({ easing: 'easeOutExpo' })
      .add({ targets: '.within-back', translateX: [-20, 0], opacity: [0, 1], duration: 600 })
      .add({ targets: '.within-caption', translateY: [20, 0], opacity: [0, 1], duration: 600 }, '-=300')
      .add({ targets: '.within-title', translateY: [80, 0], opacity: [0, 1], duration: 1200 }, '-=400')
      .add({ targets: '.within-sub', translateY: [20, 0], opacity: [0, 1], duration: 600 }, '-=800')
      .add({ targets: '.within-meta', translateY: [20, 0], opacity: [0, 1], duration: 600 }, '-=400')
      .add({ targets: '.within-cta', scale: [0.95, 1], opacity: [0, 1], duration: 500 }, '-=200');
  }, []);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    
    setTimeout(() => {
      anime({
        targets: '.artist-card',
        translateY: [24, 0],
        opacity: [0, 1],
        duration: 600,
        delay: anime.stagger(80),
        easing: 'easeOutExpo',
      });
    }, 800);
  }, []);

  return (
    <div style={{ position: 'relative', zIndex: 1, paddingTop: '64px' }}>
      {/* OG Meta Tags for WITHIN */}
      <Helmet>
        <title>WITHIN // 26.02.2026 // Brussels Planetarium</title>
        <meta property="og:title" content="WITHIN // 26.02.2026 // Brussels Planetarium" />
        <meta property="og:description" content="A 360° immersive audiovisual experience under the dome." />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_ac09e30e-ff84-4be5-9a8e-38b36c255d06/artifacts/yqc71nna_FromNow2.jpg" />
        <meta property="og:url" content="https://vaguevisionsound.com/events/within" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="WITHIN // 26.02.2026 // Brussels Planetarium" />
        <meta name="twitter:description" content="A 360° immersive audiovisual experience under the dome." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_ac09e30e-ff84-4be5-9a8e-38b36c255d06/artifacts/yqc71nna_FromNow2.jpg" />
      </Helmet>

      {/* Hero with wave painting background - more visible */}
      <section ref={heroRef} style={{ 
        minHeight: '80vh', position: 'relative', 
        display: 'flex', alignItems: 'center',
        background: `linear-gradient(to bottom, rgba(2, 8, 20, 0.55) 0%, rgba(2, 8, 20, 0.72) 60%, #020814 100%), url(${WAVE_BG}) center center / cover no-repeat`,
      }} data-testid="within-hero">
        <div style={{ padding: '80px 48px', width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
          <Link to="/events" className="within-back" data-testid="within-back"
            style={{ 
              display: 'inline-flex', alignItems: 'center', gap: '8px', 
              color: 'var(--vvs-muted)', textDecoration: 'none', 
              fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase', 
              fontFamily: 'Space Grotesk', marginBottom: '48px', opacity: 0, 
              transition: 'color 0.3s' 
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#F0F4F8'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--vvs-muted)'}
          >
            <ChevronLeft size={14} /> Events
          </Link>

          <p className="within-caption caption" style={{ marginBottom: '20px', color: 'var(--vvs-cyan)', opacity: 0 }}>
            26 February 2026 // Planetarium, Brussels
          </p>

          <h1 className="within-title neon-heading" style={{
            fontSize: 'clamp(72px, 18vw, 200px)',
            lineHeight: 0.8, letterSpacing: '-0.02em', textTransform: 'uppercase',
            margin: '0 0 36px', opacity: 0,
          }}>
            WITHIN
          </h1>

          <p className="within-sub" style={{
            fontFamily: 'Space Grotesk', fontSize: 'clamp(14px, 2vw, 18px)',
            color: 'var(--vvs-cyan)', letterSpacing: '0.15em', textTransform: 'uppercase',
            marginBottom: '40px', opacity: 0,
          }}>
            360° Immersive Audiovisual Experience
          </p>

          <div className="within-meta" style={{ 
            display: 'flex', flexWrap: 'wrap', gap: '40px', alignItems: 'baseline', 
            marginBottom: '40px', opacity: 0 
          }}>
            <div>
              <p className="caption" style={{ marginBottom: '6px' }}>Venue</p>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: '16px', color: 'var(--vvs-steel)' }}>
                Planetarium // Royal Observatory of Belgium
              </p>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: '13px', color: 'var(--vvs-muted)' }}>
                Brussels
              </p>
            </div>
            <div>
              <p className="caption" style={{ marginBottom: '6px' }}>Capacity</p>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: '16px', color: 'var(--vvs-steel)' }}>
                Limited
              </p>
            </div>
          </div>

          <div className="within-cta" style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', opacity: 0 }}>
            <Link to="/events/within/gallery" className="btn-metallic btn-metallic-filled" data-testid="within-gallery-btn"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              VIEW GALLERY <ChevronRight size={14} />
            </Link>
            <a href="https://vague-vision-sound.fairtix.io/p/regular-sale" target="_blank" rel="noopener noreferrer"
              className="btn-metallic" data-testid="within-tickets"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              {t('events.get_tickets')} <ExternalLink size={12} />
            </a>
            <a href="https://www.instagram.com/vaguevisionsound/" target="_blank" rel="noopener noreferrer"
              className="btn-metallic" data-testid="within-instagram"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <Instagram size={13} /> @vaguevisionsound
            </a>
          </div>
        </div>
      </section>

      {/* Event Poster */}
      <section style={{ padding: '60px 48px', borderTop: '1px solid rgba(240,244,248,0.05)' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <img 
            src={WITHIN_POSTER}
            alt="WITHIN - 26 February 2026 - Brussels Planetarium"
            style={{
              width: '100%',
              maxWidth: '500px',
              height: 'auto',
              borderRadius: '8px',
              boxShadow: '0 20px 60px rgba(0,50,100,0.3), 0 10px 30px rgba(0,0,0,0.5)',
            }}
            data-testid="within-poster"
          />
        </div>
      </section>

      {/* The Story */}
      <section ref={storyRef} style={{ padding: '60px 48px', borderTop: '1px solid rgba(240,244,248,0.05)' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ 
            fontFamily: 'Space Grotesk', fontSize: 'clamp(16px, 2vw, 20px)', 
            lineHeight: 1.9, color: 'var(--vvs-steel)',
          }}>
            <p style={{ marginBottom: '28px' }}>
              In a saturated world spinning on anxiety and negative headlines, where truth and distortion blur, seeing the bigger picture feels almost impossible.
            </p>
            <p style={{ marginBottom: '28px' }}>
              That's why we invite you to reconnect together to the source of your power and influence, to one place we rarely choose to look, to the origin point of vision, impact and direction where everything begins: <span style={{ color: 'var(--vvs-cyan)', fontWeight: 600 }}>WITHIN</span>.
            </p>
            <p style={{ marginBottom: '0' }}>
              WITHIN is a 360° immersive audiovisual experience taking place on the 26th of February under the dome of the Brussels Planetarium.
            </p>
          </div>
        </div>
      </section>

      {/* About the Dome */}
      <section style={{ padding: '60px 48px', borderTop: '1px solid rgba(240,244,248,0.05)' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ 
            borderLeft: '2px solid var(--vvs-cyan)',
            paddingLeft: '32px',
          }}>
            <p style={{ 
              fontFamily: 'Syne', fontWeight: 700, fontSize: 'clamp(18px, 2.5vw, 24px)',
              color: '#F0F4F8', marginBottom: '32px', textTransform: 'uppercase',
              letterSpacing: '0.02em',
            }}>
              The Planetarium is not a concert hall.
            </p>
            <p style={{ 
              fontFamily: 'Syne', fontWeight: 800, fontSize: 'clamp(36px, 6vw, 60px)',
              color: '#F0F4F8', marginBottom: '40px', lineHeight: 0.95,
            }}>
              It is a dome.
            </p>
            <div style={{ 
              fontFamily: 'Space Grotesk', fontSize: 'clamp(14px, 1.8vw, 17px)', 
              lineHeight: 2, color: 'var(--vvs-steel)',
            }}>
              <p style={{ marginBottom: '20px' }}>
                The Brussels Planetarium is part of the Royal Observatory of Belgium. Located in Laeken, it hosts one of the most technically advanced projection domes in Europe. The dome is 23 metres in diameter.
              </p>
              <p style={{ marginBottom: '20px' }}>
                Sound doesn't come from a stage.<br />
                It comes from everywhere at once.
              </p>
              <p style={{ marginBottom: '20px', color: 'var(--vvs-cyan)' }}>
                360° spatial audio.<br />
                Full dome projection.<br />
                No front row. No back row.<br />
                Only inside.
              </p>
              <p style={{ marginBottom: '0' }}>
                A configuration that exists nowhere else.<br />
                A first for VVS.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Artist Grid - By Role */}
      <section ref={lineupRef} style={{ padding: '50px 48px 70px', borderTop: '1px solid rgba(240,244,248,0.05)' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          
          {/* MUSIC Section */}
          <div style={{ marginBottom: '48px' }}>
            <p className="caption" style={{ marginBottom: '20px', textAlign: 'center' }}>MUSIC</p>
            <div data-testid="within-music-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '2px',
              borderTop: '1px solid rgba(240,244,248,0.05)',
            }}>
              {MUSIC_ARTISTS.map((artist, i) => (
                <ArtistBox key={i} artist={artist} />
              ))}
            </div>
          </div>

          {/* VISUALS // MAPPING Section */}
          <div style={{ marginBottom: '48px' }}>
            <p className="caption" style={{ marginBottom: '20px', textAlign: 'center' }}>VISUALS // MAPPING</p>
            <div data-testid="within-visuals-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '2px',
              borderTop: '1px solid rgba(240,244,248,0.05)',
            }}>
              {VISUALS_ARTISTS.map((artist, i) => (
                <ArtistBox key={i} artist={artist} />
              ))}
            </div>
          </div>

          {/* LIGHTS Section */}
          <div>
            <p className="caption" style={{ marginBottom: '20px', textAlign: 'center' }}>LIGHTS</p>
            <div data-testid="within-lights-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '2px',
              borderTop: '1px solid rgba(240,244,248,0.05)',
            }}>
              {LIGHTS_ARTISTS.map((artist, i) => (
                <ArtistBox key={i} artist={artist} />
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* Limited capacity note */}
      <section style={{ padding: '30px 48px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ 
            padding: '24px 28px',
            background: 'rgba(0,240,255,0.03)', 
            border: '1px solid rgba(0,240,255,0.1)',
            textAlign: 'center',
          }}>
            <p style={{ 
              fontFamily: 'Space Grotesk', fontSize: '14px', 
              color: 'var(--vvs-steel)', lineHeight: 1.8, margin: 0,
            }}>
              A limited-capacity experience bringing together artists, creatives, cultural actors and curious minds.
            </p>
          </div>
        </div>
      </section>

      {/* Sponsors & Credits */}
      <section ref={creditsRef} style={{ padding: '50px 48px 60px', borderTop: '1px solid rgba(240,244,248,0.05)' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          
          {/* Sponsors */}
          <div style={{ marginBottom: '36px' }}>
            <p className="caption" style={{ marginBottom: '16px' }}>Sponsored by</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 28px', justifyContent: 'center' }}>
              {SPONSORS.map((s, i) => (
                <PartnerLink key={i} name={s.name} handle={s.instagram} />
              ))}
            </div>
          </div>

          {/* Powered by */}
          <div>
            <p className="caption" style={{ marginBottom: '16px' }}>Powered by</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 28px', justifyContent: 'center' }}>
              {POWERED_BY.map((p, i) => (
                <PartnerLink key={i} name={p.name} handle={p.instagram} />
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* Back to events */}
      <section style={{ padding: '0 48px 30px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <Link to="/events" className="btn-metallic" style={{ textDecoration: 'none', display: 'inline-block' }}>
            ← Back to Events
          </Link>
        </div>
      </section>

      {/* Sponsor Reel */}
      <SponsorReel />

    </div>
  );
}
